import { Permission } from '@act/shared/authorization';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { Button, themeColors } from '@act/storybook';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/user-monitoring';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogHeader } from 'react-components/dialog/dialog-header';
import { useForm } from 'react-hook-form';
import { surveysRequests } from 'redux/requests/surveys.requests';
import {
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { surveyActions, surveysSelectors } from 'redux/slices/surveys.slice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { makeRequest } from 'redux/utils/api-request';
import { AdditionalFeedCardActions } from 'toolkit/info-card/info-card';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { isEditable } from 'utilities/is-editable';
import { useToast } from '../app/ReusableToast';
import { TrackingProvider } from '../tracking';
import {
  AssignedSurveyContent,
  NotesForm,
  notesSchema,
} from './components/AssignedSurveyContent';
import useConfirmationAlertDialog from './hooks/use-confirmation-alert-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';

export interface AssignedSurveyDialogUniqueProps {
  assignedSurveyId: number;
  isReviewMode?: boolean;
}

export type AssignedSurveyDialogProps = GenericDialogProps<boolean> &
  AssignedSurveyDialogUniqueProps;

export type AssignedSurveyDialogCallbackInput = {};

const AssignedSurveyDialog = ({
  assignedSurveyId,
  isReviewMode = false,
  open,
  setOpen,
}: AssignedSurveyDialogProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const member: UserDto = useAppSelector(selectSelectedMember);
  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const menuOpen = Boolean(additionalMenuAnchor);
  const openAdditionalMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const closeAdditionalMenu = () => {
    setAdditionalMenuAnchor(null);
  };
  function executeHandler(handler: () => void) {
    setAdditionalMenuAnchor(null);
    handler();
  }

  const assignedSurvey: SurveyV2.AssignedSurveyDto =
    surveysSelectors.useSelectAssignedSurveyById(assignedSurveyId);

  const { control, getValues, formState } = useForm<NotesForm>({
    defaultValues: {
      notes: assignedSurvey?.notes || '',
    },
    resolver: zodResolver(notesSchema),
  });

  const [submittingForm, setSubmittingForm] = useState(false);
  const [isSurveyFormDirty, setIsSurveyFormDirty] = useState(false);
  const [updatedAssignedSurvey, _isUpdatingAssignedSurvey, updateNote] =
    useApiCall(surveysRequests.updateAssignedSurveyNotes);
  const [submittedSurvey, _submitSurveyLoading, submitSurvey] = useApiCall(
    surveysRequests.submitAssignedSurvey
  );

  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setIsSurveyFormDirty(isDirty);
  }, []);

  async function handleAutoSave(autoSaveNote: string) {
    if (autoSaveNote !== assignedSurvey?.notes) {
      updateNote({
        params: { assignedSurveyId: assignedSurvey.id },
        body: {
          notes: autoSaveNote,
        },
      });
    }
  }

  async function handleSubmit() {
    if (isSurveyFormDirty) {
      void handleUnsavedChanges(() => {
        proceedWithSave();
      });
      return;
    }
    await proceedWithSave();
  }

  async function proceedWithSave() {
    const notes = getValues('notes');
    const notesChanged = notes !== (assignedSurvey?.notes || '');
    const shouldSubmitSurvey = !isReviewMode && !assignedSurvey?.completedAt;
    const hasChanges = notesChanged || shouldSubmitSurvey;

    // If there's nothing to save, just close the dialog
    if (!hasChanges) {
      setOpen(false);
      return;
    }

    setSubmittingForm(true);

    // Only submit survey if not in review mode and survey is not already completed
    // The backend will handle whether there's actually anything to submit
    if (shouldSubmitSurvey) {
      submitSurvey({
        params: { assignedSurveyId: assignedSurvey.id },
        body: null,
      });
    }

    // Only update notes if they've changed
    if (notesChanged) {
      updateNote({
        params: { assignedSurveyId: assignedSurvey.id },
        body: { notes },
      });
    }
  }

  useEffect(() => {
    if (submittedSurvey) {
      dispatch(surveyActions.upsertAssignedSurvey(submittedSurvey));
      setOpen(false);
      toast('Succesfully saved survey');
    }
    if (submittingForm && updatedAssignedSurvey) {
      const currentAssignedSurvey = {
        ...assignedSurvey,
        ...{ notes: updatedAssignedSurvey.notes },
      };
      dispatch(surveyActions.upsertAssignedSurvey(currentAssignedSurvey));
      setOpen(false);
      toast('Successfully updated survey');
    }
  }, [submittedSurvey, updatedAssignedSurvey]);

  function handleClose() {
    if (isSurveyFormDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  }
  const canReopenSurvey = membersAppSelectors.useHasPermissionOnMember(
    assignedSurvey?.memberId,
    Permission.CAN_REOPEN_SURVEYS
  );

  const menuItems = useMemo(() => {
    const items: AdditionalFeedCardActions[] = [];

    if (
      canReopenSurvey &&
      assignedSurvey?.completedAt &&
      isEditable(assignedSurvey.completedAt)
    ) {
      items.push({
        title: 'Reopen Survey',
        handler: async () => {
          const shouldWeReopenSurvey = await openConfirmationAlertDialog({
            title: 'Confirm reopening',
            icon: <ErrorOutlineIcon />,
            iconColor: 'error',
            iconBorderColor: 'errorContainer',
            avatarCaption: 'Are you sure?',
            message: 'Are you sure you want to reopen this survey?',
            confirmationText: 'Yes, reopen it',
            confirmButtonColor: 'error',
          });
          if (shouldWeReopenSurvey) {
            const updatedAssignedSurvey = await makeRequest(
              surveysRequests.reopenAssignedSurvey,
              {
                params: { assignedSurveyId: assignedSurvey.id },
                body: null,
              }
            );

            dispatch(surveyActions.upsertAssignedSurvey(updatedAssignedSurvey));
            handleClose();
          }
        },
      });
    }
    return items;
  }, [canReopenSurvey, assignedSurvey]);

  return (
    <TrackingProvider contextName="assigned-survey-dialog">
      <Dialog
        maxWidth="md"
        open={open}
        onClose={handleClose}
        scroll="body"
        aria-labelledby="assinged-survey-dialog"
        aria-describedby="assigned-survey-dialog"
        role="dialog"
      >
        <DialogHeader
          postfixAction={{
            title: 'close',
            callback: handleClose,
            icon: <CloseIcon />,
          }}
        >
          <Box display="flex" flexDirection="row">
            {`${isReviewMode ? 'Reviewing s' : 'S'}urvey for ${
              member.firstName
            } ${member.lastName.slice(0, 1)}`}
            {menuItems?.length > 0 && (
              <div className="actions" style={{ marginLeft: 'auto' }}>
                <IconButton
                  aria-label="settings"
                  onClick={openAdditionalMenu}
                  sx={{ margin: '-8px', color: themeColors.onSurfaceVariant }}
                  {...generateDatadogTrackingProps('assigned-survey-settings')}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={additionalMenuAnchor}
                  open={menuOpen}
                  onClose={closeAdditionalMenu}
                >
                  {menuItems.map((item, index) => (
                    <MenuItem
                      onClick={() => {
                        executeHandler(item.handler);
                      }}
                      key={`menu-item-${index}`}
                      {...generateDatadogTrackingProps(
                        `assigned-survey-settings-select-${item.title}`
                      )}
                    >
                      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                      <ListItemText>{item.title}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )}
          </Box>
        </DialogHeader>
        <DialogContent sx={{ width: '525px', padding: '20px 12px 12px 24px' }}>
          <DialogContentText component="div">
            <AssignedSurveyContent
              assignedSurvey={assignedSurvey}
              member={member}
              isReviewMode={isReviewMode}
              submittingForm={submittingForm}
              onFormStateChange={handleFormStateChange}
              control={control}
              formState={formState}
              handleAutoSave={handleAutoSave}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ padding: '8px 32px 8px 24px', marginBottom: '8px' }}
        >
          <Button
            variant="contained"
            type="submit"
            debounced
            onClick={handleSubmit}
            sx={{ padding: '8px 24px' }}
            {...generateDatadogTrackingProps('save-and-close')}
          >
            Save and close
          </Button>
        </DialogActions>
      </Dialog>
    </TrackingProvider>
  );
};

export default AssignedSurveyDialog;
